interface ChatTimestampFormatOptions {
  now?: number | Date;
  locale?: string;
  timeZone?: string;
}

interface CalendarDayParts {
  year: number;
  month: number;
  day: number;
}

function getCalendarDayParts(date: Date, locale: string, timeZone?: string): CalendarDayParts {
  const formatter = new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone,
  });
  const parts = formatter.formatToParts(date);
  return {
    year: Number(parts.find((part) => part.type === 'year')?.value),
    month: Number(parts.find((part) => part.type === 'month')?.value),
    day: Number(parts.find((part) => part.type === 'day')?.value),
  };
}

function getPreviousCalendarDayParts(parts: CalendarDayParts): CalendarDayParts {
  const previousDate = new Date(Date.UTC(parts.year, parts.month - 1, parts.day - 1));
  return {
    year: previousDate.getUTCFullYear(),
    month: previousDate.getUTCMonth() + 1,
    day: previousDate.getUTCDate(),
  };
}

function areSameCalendarDay(left: CalendarDayParts, right: CalendarDayParts): boolean {
  return left.year === right.year && left.month === right.month && left.day === right.day;
}

export function formatChatMessageTimestamp(
  timestamp: number,
  options: ChatTimestampFormatOptions = {}
): string {
  const locale = options.locale ?? undefined;
  const comparisonLocale = options.locale ?? 'en-US';
  const messageDate = new Date(timestamp);
  const nowDate = options.now instanceof Date ? options.now : new Date(options.now ?? Date.now());
  const time = messageDate.toLocaleTimeString(locale, {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: options.timeZone,
  });

  const messageDay = getCalendarDayParts(messageDate, comparisonLocale, options.timeZone);
  const today = getCalendarDayParts(nowDate, comparisonLocale, options.timeZone);

  if (areSameCalendarDay(messageDay, today)) {
    return `Today, ${time}`;
  }

  if (areSameCalendarDay(messageDay, getPreviousCalendarDayParts(today))) {
    return `Yesterday, ${time}`;
  }

  const date = messageDate.toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    timeZone: options.timeZone,
  });
  return `${date}, ${time}`;
}
